import { toast } from "react-toastify";
// ProjectHistoryModal - りれきの かくにん（じゆうにつくるの記録。決まりは docs/edit-history-undo.md）
// 1 つの一覧に、区切り（開いた・閉じた・10 分ごと・保存ボタン・公開・公開の更新・restore）を新しい順にカードで並べ、
// その下に「その区切りまでに やったこと」を並べる。最後の区切りのあとに やったことは、一番上の「いま」の下に並べる。
// 「もどす」（restore）は区切りのカードから。公開中のアプリはドラフトに戻るので、みんなに見える版は「こうかいを こうしん」まで変わらない。
// restore は 1 つの操作として undo で取り消せる。右上の「名前をつけて保存」で、いまの中身を名前つきで記録に残せる。
// 見られる件数はプランで決まる（未課金は新しい 10 区切り分）。
// 高さは中身に関係なく固定（状態の行・リスト・件数の注記はいつも同じ場所・同じ高さを占める）。
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { CloseBtn, FadeTransition, ConfirmMessageBox } from "common/elements";
import { flushEditHistory } from "features/creator/editHistory";
import { actions, selectProjectById } from "features/creator/slice";
import {
  getProjectVersions,
  createProjectVersion,
  fetchProjectContent,
  flushProjectContent,
  restoreProjectVersion,
} from "features/creator/api";
import {
  ProjectVersion,
  ProjectEditAction,
  ProjectVersionReason,
  ProjectEditHistoryEntry,
  ProjectVersionsResponse,
} from "features/creator/types";
import { ProjectUser } from "./ProjectUser";
import { SaveVersionNameDialog } from "./SaveVersionNameDialog";

const REASON: Record<ProjectVersionReason, { labelKey: string; icon: string; badge: string }> = {
  [ProjectVersionReason.COMPLETE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_COMPLETE",
    icon: "check_circle",
    badge: "bg-green-100",
  },
  [ProjectVersionReason.PUBLISH_UPDATE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_PUBLISH_UPDATE",
    icon: "published_with_changes",
    badge: "bg-orange-100",
  },
  [ProjectVersionReason.PUBLISH]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_PUBLISH",
    icon: "public",
    badge: "bg-orange-100",
  },
  [ProjectVersionReason.CLOSE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_CLOSE",
    icon: "save",
    badge: "bg-blue-100",
  },
  [ProjectVersionReason.OPEN]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_OPEN",
    icon: "edit_document",
    badge: "bg-blue-100",
  },
  [ProjectVersionReason.BEFORE_RESTORE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_BEFORE_RESTORE",
    icon: "history",
    badge: "bg-blue-100",
  },
  [ProjectVersionReason.MIGRATE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_MIGRATE",
    icon: "flag",
    badge: "bg-orange-100",
  },
  [ProjectVersionReason.AUTO]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_AUTO",
    icon: "schedule",
    badge: "bg-blue-100",
  },
  [ProjectVersionReason.RESTORE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_RESTORE",
    icon: "history",
    badge: "bg-orange-100",
  },
  [ProjectVersionReason.SAVE]: {
    labelKey: "MSG_CREATOR_HISTORY_REASON_SAVE",
    icon: "save",
    badge: "bg-green-100",
  },
};

/** 部品の種類 → 名前の文言（textinput は FORM、rectangle は RECT） */
const COMPONENT_NAME_KEY: Record<string, string> = {
  screen: "SCREEN",
  button: "BUTTON",
  label: "LABEL",
  textinput: "FORM",
  checkbox: "CHECKBOX",
  switch: "SWITCH",
  image: "IMAGE",
  stamp: "STAMP",
  drawing: "DRAWING",
  circle: "CIRCLE",
  line: "LINE",
  rectangle: "RECT",
  star: "STAR",
  triangle: "TRIANGLE",
};

const EDIT: Record<ProjectEditAction, { labelKey: string; icon: string }> = {
  [ProjectEditAction.ADD_COMPONENT]: {
    labelKey: "MSG_CREATOR_HISTORY_EDIT_ADD",
    icon: "add_circle",
  },
  [ProjectEditAction.REMOVE_COMPONENT]: {
    labelKey: "MSG_CREATOR_HISTORY_EDIT_REMOVE",
    icon: "delete",
  },
  [ProjectEditAction.ADD_SCREEN]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_ADD", icon: "note_add" },
  [ProjectEditAction.REMOVE_SCREEN]: {
    labelKey: "MSG_CREATOR_HISTORY_EDIT_REMOVE",
    icon: "delete",
  },
  [ProjectEditAction.CHANGE_PROPERTY]: {
    labelKey: "MSG_CREATOR_HISTORY_EDIT_CHANGE",
    icon: "edit",
  },
  [ProjectEditAction.CHANGE_BLOCKLY]: {
    labelKey: "MSG_CREATOR_HISTORY_EDIT_CHANGE_BLOCKLY",
    icon: "extension",
  },
  [ProjectEditAction.CHANGE_CANVAS]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_CHANGE", icon: "edit" },
  [ProjectEditAction.PASTE]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_PASTE", icon: "content_paste" },
  [ProjectEditAction.UNDO]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_UNDO", icon: "undo" },
  [ProjectEditAction.REDO]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_REDO", icon: "redo" },
  [ProjectEditAction.RESTORE]: { labelKey: "MSG_CREATOR_HISTORY_EDIT_RESTORE", icon: "history" },
};

const targetText = (entry: ProjectEditHistoryEntry) => {
  if (entry.target_name) return `「${entry.target_name}」`;
  const key = entry.target_type ? COMPONENT_NAME_KEY[entry.target_type] : undefined;
  return key
    ? I18n.t(`MSG_CREATOR_COMPONENT_NAME_${key}`)
    : I18n.t("MSG_CREATOR_HISTORY_TARGET_COMPONENTS");
};

const editLabel = (entry: ProjectEditHistoryEntry): string => {
  // undo / redo は、何を取り消した・やり直したかを添える（「もとにもどした：ボタンを ついか」）
  if (
    (entry.action_type === ProjectEditAction.UNDO ||
      entry.action_type === ProjectEditAction.REDO) &&
    entry.undone_action_type
  ) {
    return I18n.t(
      entry.action_type === ProjectEditAction.UNDO
        ? "MSG_CREATOR_HISTORY_EDIT_UNDO_OF"
        : "MSG_CREATOR_HISTORY_EDIT_REDO_OF",
      {
        action: editLabel({
          ...entry,
          action_type: entry.undone_action_type,
          undone_action_type: null,
        }),
      },
    );
  }
  return I18n.t(EDIT[entry.action_type]?.labelKey ?? "MSG_CREATOR_HISTORY_EDIT_CHANGE", {
    target: targetText(entry),
  });
};

/** 区切りのカードと、その下に並べる やったこと（その区切りまでに やったこと） */
type HistoryGroup = { version: ProjectVersion | null; edits: ProjectEditHistoryEntry[] };

/** 新しい順の区切りと やったことを、区切りごとにまとめる。先頭は「いま」（最後の区切りのあとの分） */
const groupHistory = (data: ProjectVersionsResponse): HistoryGroup[] => {
  const time = (value: string) => new Date(value).getTime();
  const groups: HistoryGroup[] = [{ version: null, edits: [] }];
  data.versions.forEach((version) => groups.push({ version, edits: [] }));
  data.history.forEach((entry) => {
    // やったことは、そのあとにできた区切りのうち一番古いものに入れる（同じ秒の区切りも「あと」とみなす）。
    // 区切りは新しい順なので、やったことより古い最初の区切りの 1 つ手前。どの区切りより新しければ「いま」（0）
    const older = data.versions.findIndex((v) => time(v.created_at) < time(entry.created_at));
    groups[older < 0 ? data.versions.length : older].edits.push(entry);
  });
  return groups;
};

const DEFAULT_CAPTURE = `${Constants.assetHost}/assets/images/default_capture.png`;

/** 記録の時刻（カード・戻す確認・戻したトーストで同じ書き方にする） */
const versionTime = (version: ProjectVersion) => dayjs(version.created_at).format("M/D HH:mm");

export const ProjectHistoryModal = ({
  visible,
  close,
}: {
  visible: boolean;
  close: () => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const uid = useSelector((state: RootState) => state.user.active?.uid);
  const projectId = useSelector((state: RootState) => state.creator.editingProjectId);
  const selectedScreenId = useSelector((state: RootState) => state.creator.selectedScreenId);
  const project = useSelector((state: RootState) => selectProjectById(state, projectId));
  const [data, setData] = useState<ProjectVersionsResponse | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<ProjectVersion | null>(null);
  const [saving, setSaving] = useState(false);
  const [naming, setNaming] = useState(false);
  // 保存ボタンで残した記録には、押した本人のアイコンと名前を出す（記録を残せるのは作者本人だけ）
  const user = useSelector((state: RootState) => state.user.active);

  // 開くたびに、待っている保存と変更履歴を送ってから読み直す（いまの操作まで一覧に出す）
  useEffect(() => {
    if (!visible || !uid || !projectId) return;
    setData(null);
    const load = async () => {
      await flushProjectContent();
      await flushEditHistory();
      setData(await getProjectVersions({ uid, project_id: projectId }));
    };
    load().catch(() => setData(null));
  }, [visible, uid, projectId]);

  const handleClose = () => {
    play();
    btnDelay(() => close());
  };

  const failed = () =>
    toast.warn(
      <div>
        <p className="type-text type-sm text-textcolor-black">{I18n.t("MSG_TOAST_FAILED")}</p>
      </div>,
    );

  // 戻したあと・すてたあとは、エディタの中身を差し替える（Blockly も state から描き直す）。
  // 開き直すと undo の履歴が消えるので、差し替えを 1 つの操作として積み、undo で取り消せるようにする
  const replaceEditor = async (action_type: ProjectEditAction) => {
    const { content, has_draft } = await fetchProjectContent({ project_id: projectId });
    dispatch(
      actions.replaceContentWithUndo({
        content: { ...content, selectedScreenId: selectedScreenId },
        label: { action_type },
      }),
    );
    // 画面を選んでいるページに合わせ直すのは replaceContentWithUndo の中でする（ここでもう一度切り替えると打ち消し合う）
    dispatch(actions.updateProjectDraftState({ project_id: projectId, has_draft }));
  };

  const handleRestoreConfirm = async () => {
    const target = restoreTarget;
    setRestoreTarget(null);
    if (!target) return;
    try {
      await flushProjectContent();
      await flushEditHistory();
      await restoreProjectVersion({
        uid,
        project_id: projectId,
        version_id: target.id,
      });
      await replaceEditor(ProjectEditAction.RESTORE);
      toast.success(
        <div>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_CREATOR_TOAST_RESTORED", { time: versionTime(target) })}
          </p>
        </div>,
      );
      close();
    } catch {
      failed();
    }
  };

  // 「名前をつけて保存」：押したら名前を入れるダイアログを出す
  const handleSave = () => {
    play();
    btnDelay(() => setNaming(true));
  };

  // 名前が決まったら、いまの中身で記録を 1 つ残す（中身が前と同じでも残す）
  const handleSaveWithName = async (name: string) => {
    setNaming(false);
    if (saving) return;
    setSaving(true);
    try {
      await flushProjectContent();
      await flushEditHistory();
      await createProjectVersion({
        uid,
        project_id: projectId,
        reason: ProjectVersionReason.SAVE,
        name,
      });
      setData(await getProjectVersions({ uid, project_id: projectId }));
      toast.success(
        <div>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_CREATOR_TOAST_SAVED")}
          </p>
        </div>,
      );
    } catch {
      failed();
    } finally {
      setSaving(false);
    }
  };
  const groups = data ? groupHistory(data) : [];
  const limited = !!data && data.limit >= 0 && data.total > data.versions.length;

  return (
    <>
      <Dialog
        open={visible}
        maxWidth={false}
        sx={{ zIndex: 20010 }}
        TransitionComponent={FadeTransition}
        // 後ろはピースビルダーの記録のモーダルと同じ灰色（textcolor-black の 50%）
        componentsProps={{ backdrop: { className: "!bg-textcolor-black/50" } }}
        PaperProps={{
          style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
          elevation: 0,
        }}
      >
        <div className="flex-col-el flex-center relative" style={{ transform: `scale(${scale})` }}>
          {/* ほかのモーダル（アプリの せってい など）と同じ影で、後ろのエディタから浮かせる */}
          <div
            className="flex-col-el w-[720px] border-solid border-white border-[8px] rounded-lg bg-white box-border"
            style={{
              filter:
                "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
            }}
          >
            <CloseBtn close={handleClose} />
            <div className="flex-col-el items-stretch gap-3 p-4 rounded bg-beige-60">
              {/* 題名と保存ボタン（保存は右上。角の閉じるボタンに近づきすぎないよう右を少し空ける） */}
              <div className="flex-row-el items-center justify-between w-full gap-2 pr-4 box-border">
                <div className="flex-row-el items-center gap-2">
                  <span className="material-symbols-outlined type-title text-blue-100">
                    history
                  </span>
                  <p className="type-text-left type-title text-textcolor-black">
                    {I18n.t("MSG_CREATOR_HISTORY_TITLE")}
                  </p>
                </div>
                <div className="upsilon shrink-0">
                  <button
                    type="button"
                    disabled={saving || !data}
                    onClick={handleSave}
                    className="type-inherit-font btn btn-primary whitespace-nowrap"
                  >
                    <div className="flex-row-center gap-1">
                      <span className="material-symbols-outlined text-white type-sm">save</span>
                      <span className="type-text type-sm text-white">
                        {I18n.t("MSG_CREATOR_HISTORY_SAVE_BTN")}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* リストは高さ固定：件数・空に関わらず常に h-[440px] */}
              <div className="flex-col-el items-stretch w-full h-[440px]">
                {data && data.versions.length + data.history.length > 0 ? (
                  <div className="flex-col-el items-stretch w-full h-full gap-2 pr-1 pb-1 box-border overflow-y-auto invisible-scrollbar">
                    {groups.map((group, index) => {
                      const version = group.version;
                      // 「いま」は最後の区切りのあとに やったことがあるときだけ出す
                      if (!version && group.edits.length === 0) return null;
                      // 一番新しい区切りのあとに何もしていなければ、その区切りがいまの中身（もどす は出さない）
                      const isCurrent = !!version && index === 1 && groups[0].edits.length === 0;
                      const reason =
                        REASON[version?.reason ?? ProjectVersionReason.CLOSE] ??
                        REASON[ProjectVersionReason.CLOSE];
                      return (
                        <div
                          key={version?.id ?? "now"}
                          className="flex-col-el items-stretch w-full gap-1"
                        >
                          {version ? (
                            <div className="flex-row-el items-center w-full gap-3 bg-white rounded-xl p-3 box-border border-solid border-2 border-beige-alt drop-shadow-normal-show">
                              {version.reason === ProjectVersionReason.SAVE ? (
                                // 自分で保存した記録は、キャプチャの代わりに保存した本人のアイコンと名前を出す。
                                // 見た目はアプリの作者の表示（ProjectUser：アイコンに名前が重なる）と同じ
                                <div className="flex-col-el flex-center relative w-24 h-16 shrink-0">
                                  <div className="relative scale-75">
                                    <ProjectUser name={user?.display_name} icon={user?.icon} />
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={version.capture || DEFAULT_CAPTURE}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = DEFAULT_CAPTURE;
                                  }}
                                  // 中身が白いアプリでもカードの白と見分けられるように枠をつける
                                  className="w-24 h-16 shrink-0 object-cover rounded-lg bg-beige-60 border-solid border-2 border-gray2-60 box-border"
                                />
                              )}
                              <div className="flex-col-el items-start flex-1 min-w-0 gap-1">
                                <div className="flex-row-el items-center gap-2 w-full">
                                  <span
                                    className={`flex-col-center shrink-0 w-8 h-8 rounded-lg ${reason.badge}`}
                                  >
                                    <span className="material-symbols-outlined text-white type-sm">
                                      {reason.icon}
                                    </span>
                                  </span>
                                  {/* 「名前をつけて保存」の記録は、つけた名前を出す（長ければ 1 行で「…」）。
                                      type-text-left の flex-shrink: 0 と white-space: pre-line を上書きしないと縮まない */}
                                  <span className="type-text-left type-body text-textcolor-black truncate min-w-0 !shrink !whitespace-nowrap">
                                    {version.reason === ProjectVersionReason.SAVE
                                      ? version.name
                                      : I18n.t(reason.labelKey)}
                                  </span>
                                </div>
                                <div className="flex-row-el items-center gap-2">
                                  {version.completed_version != null && (
                                    <span className="flex-col-center shrink-0 rounded-full bg-blue-10 px-2 type-sm text-blue-100 whitespace-nowrap">
                                      {`V${version.completed_version}`}
                                    </span>
                                  )}
                                  <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                                    {versionTime(version)}
                                  </span>
                                </div>
                              </div>
                              {isCurrent ? (
                                <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                                  {I18n.t("MSG_CREATOR_HISTORY_NOW")}
                                </span>
                              ) : (
                                <div className="upsilon shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      play();
                                      btnDelay(() => setRestoreTarget(version));
                                    }}
                                    className="type-inherit-font btn btn-blue whitespace-nowrap"
                                  >
                                    <div className="flex-row-center gap-1">
                                      <span className="material-symbols-outlined text-white type-sm">
                                        undo
                                      </span>
                                      <span className="type-text type-sm text-white">
                                        {I18n.t("MSG_CREATOR_HISTORY_RESTORE")}
                                      </span>
                                    </div>
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            // 最後の区切りのあとに やったこと（いまの中身）
                            <div className="flex-row-el items-center w-full gap-2 py-1">
                              <span className="flex-col-center shrink-0 rounded-full bg-green-100 px-3 py-1 type-sm text-white whitespace-nowrap">
                                {I18n.t("MSG_CREATOR_HISTORY_NOW")}
                              </span>
                              <div className="flex-1 h-1 rounded-full bg-beige-alt" />
                            </div>
                          )}
                          {/* その区切りまでに やったこと。区切りのカードと見分けられるよう、線で下げて小さく並べる */}
                          {group.edits.length > 0 && (
                            <div className="flex-col-el items-stretch gap-1 ml-6 pl-3 border-solid border-0 border-l-4 border-beige-alt box-border">
                              {group.edits.map((entry) => (
                                <div
                                  key={entry.id}
                                  className="flex-row-el items-center w-full gap-2"
                                >
                                  <div className="flex-col-center shrink-0 w-8 h-8 rounded-lg bg-blue-100">
                                    <span className="material-symbols-outlined text-white type-sm">
                                      {EDIT[entry.action_type]?.icon ?? "edit"}
                                    </span>
                                  </div>
                                  <span className="type-text-left type-sm text-textcolor-black flex-1 min-w-0 truncate">
                                    {editLabel(entry)}
                                  </span>
                                  {entry.count > 1 && (
                                    <span className="flex-col-center shrink-0 rounded-full bg-blue-10 px-2 type-sm text-blue-100 whitespace-nowrap">
                                      {`×${entry.count}`}
                                    </span>
                                  )}
                                  <span className="type-text-left type-sm text-textcolor-black/50 whitespace-nowrap">
                                    {dayjs(entry.created_at).format("HH:mm")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex-col-el flex-center w-full h-full">
                    <span className="type-text type-body text-textcolor-black/40">
                      {data ? I18n.t("MSG_CREATOR_HISTORY_EMPTY") : ""}
                    </span>
                  </div>
                )}
              </div>

              {/* 見られる件数の注記（高さ固定。制限が無いときも同じ高さを空けておく） */}
              <div className="flex-row-el items-center w-full h-8">
                <span
                  className={`type-text-left type-sm text-textcolor-black/60 ${
                    limited ? "" : "invisible"
                  }`}
                >
                  {I18n.t("MSG_CREATOR_HISTORY_LIMIT_NOTE", {
                    limit: data?.limit ?? 0,
                    total: data?.total ?? 0,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <SaveVersionNameDialog
        visible={naming}
        close={() => setNaming(false)}
        save={handleSaveWithName}
      />

      {restoreTarget && (
        <ConfirmMessageBox
          // どの時刻の記録に戻すかを出す（トーストも同じ時刻を出す）
          title={I18n.t("MSG_CREATOR_HISTORY_RESTORE_CONFIRM_TITLE")}
          message={I18n.t(
            project?.published
              ? "MSG_CREATOR_HISTORY_RESTORE_CONFIRM_MESSAGE_PUBLISHED"
              : "MSG_CREATOR_HISTORY_RESTORE_CONFIRM_MESSAGE",
            { time: versionTime(restoreTarget) },
          )}
          close={() => setRestoreTarget(null)}
          confirm={handleRestoreConfirm}
        />
      )}
    </>
  );
};
